import {IBundleIncludedSsrViewModel} from "./bundle-included-ssr-view-model.interface";
import {ISsrType} from "../../../ssr-types/ssr-types.service.interface";
import {BundleSsrUpgradeModel} from "./bundle-ssr-upgrade.model";
import {BundleModel} from "./bundle.model";
import {computed, makeObservable} from "mobx";
import {YesNoDialogPrimaryButton, YesNoDialogResult} from "../../../dialog-factory/yes-no-dialog.enums";

export class BundleIncludedSsrModel implements IBundleIncludedSsrViewModel {
    constructor(ssrType: ISsrType,
                private readonly bundle: BundleModel,
                ssrAliasesInBundle: ISsrType[],
                availableUpgrades: BundleSsrUpgradeModel[]) {
        this.originalSsrType = ssrType;
        this._ssrAliasesInBundle = ssrAliasesInBundle;
        this._availableUpgrades = availableUpgrades;
        makeObservable(this, {
            upgradedToSsrType: computed,
            quantityToDisplay: computed
        })
    }

    public readonly originalSsrType: ISsrType;

    get currentSsrType(): ISsrType {
        const upgradedTo = this.upgradedToSsrType;
        if(upgradedTo) {
            return upgradedTo;
        }
        return this.originalSsrType;
    }

    private readonly _ssrAliasesInBundle: ISsrType[];

    get ssrAliasesInBundle(): ISsrType[] {
        if(this.upgradedToSsrType) {
            return [];
        }
        return this._ssrAliasesInBundle;
    }

    private readonly _availableUpgrades: BundleSsrUpgradeModel[];

    get availableUpgradesToDisplay(): BundleSsrUpgradeModel[] {
        if(this.upgradedToSsrType) {
            return [];
        }

        if(!this.bundle.isSelected) {
            return [];
        }

        if(this.bundle.journey.journeyKey !== this.bundle.booking.departureJourney?.journeyKey) {
            return [];
        }

        return this._availableUpgrades;
    }

    getAvailableUpgrade(toSsrType: ISsrType): BundleSsrUpgradeModel | null {
        return this._availableUpgrades.find(u => u.toSsrType.ssrCode === toSsrType.ssrCode) || null;
    }

    get allowDowngrade(): boolean {
        return Boolean(this.upgradedToSsrType) && this.bundle.journey.journeyKey === this.bundle.booking.departureJourney?.journeyKey;
    }

    async downgrade(): Promise<void> {
        if(!this.allowDowngrade) {
            throw new Error("There is nothing downgrade for this bundle SSR");
        }

        const dialogResult = await this.bundle.services.dialogFactory.showYesNoDialog({
            title: this.bundle.services.language.translate('Downgrade confirmation'),
            message: this.bundle.services.language.translate('Are you sure you want to downgrade?'),
            primaryButton: YesNoDialogPrimaryButton.PrimaryButtonNo
        });

        if(dialogResult !== YesNoDialogResult.Yes) {
            return;
        }

        await this.bundle.downgradeBundleSsr(this);

    }

    get quantityToDisplay(): number {

        if(this.currentSsrType.aliasFor) {
            return 0;
        }

        if(this.bundle.isSelected && !this.bundle.journey.isBundleTemporarySelected) {
            const firstPassengerSegment = this.bundle.journey.getAllPassengersSegments()[0];
            if(firstPassengerSegment) {
                let passengerInBundleSsr = firstPassengerSegment.getInBundleSsr(this.currentSsrType.ssrCode);
                if(!passengerInBundleSsr) {
                    return 0;
                }
            }
        }


        return 1 + this.ssrAliasesInBundle.length;
    }

    get upgradedToSsrType(): ISsrType | null {
        if(!this.bundle.isSelected) {
            return null;
        }

        const firstPassengerSegment = this.bundle.journey.getAllPassengersSegments()[0];
        if(!firstPassengerSegment) {
            return null;
        }

        let passengerInBundleSsr = firstPassengerSegment.getInBundleSsr(this.originalSsrType.ssrCode);

        //if the originalSsrType exists on passenger segment and is marked as inBundle then it is not upgraded
        if(passengerInBundleSsr) {
            return null;
        }

        for(let availableUpgrade of this._availableUpgrades) {
            passengerInBundleSsr = firstPassengerSegment.getInBundleSsr(availableUpgrade.toSsrType.ssrCode);
            if(passengerInBundleSsr) {
                return availableUpgrade.toSsrType;
            }
        }

        return null;
    }

}

